import LessonEditorApi from "@apis/editor.lesson.api";
import { isEmpty } from "@utils/var.check.ts";
import NotebookApi from "@modules/notebook-editor/notebook-editor-api.ts";
import { defineStore } from "pinia";
import { computed, reactive } from "vue";
import {
  LessonActivityAnchorModel,
  LessonActivityComponentAnchorModel,
} from "@plugins/dynamic-link/types.ts";

const initState = () =>
  reactive({
    lessonUuid: "",
    notebookUuid: "",
    lessonActivityAnchors: [] as LessonActivityAnchorModel[],
    lessonActivityComponentAnchors: [] as LessonActivityComponentAnchorModel[],
    notebookPageAnchors: [],
    showSectionActivityNumber: true,
    showCover: true,
    showSectionCover: true,
    navigation: {
      type: "",
      id: "",
    },
  });

const pageSortFunc = (x, y) => {
  return x.pageNo - y.pageNo;
};

const componentSortFunc = (x, y) => {
  return x.indexNo - y.indexNo;
};

const state = initState();

export const useDynamicLinkStore = defineStore("dynamicLink", () => {
  const getSortedAnchorNotebookPages = computed(() => state.notebookPageAnchors);
  const getShowSectionActivityNumber = computed(() => state.showSectionActivityNumber);
  const getShowCover = computed(() => state.showCover);
  const getShowSectionCover = computed(() => state.showSectionCover);
  const getLessonUuid = computed(() => state.lessonUuid);
  const getNotebookUuid = computed(() => state.notebookUuid);
  const getNavigationEvent = computed(() => state.navigation);

  const getAnchorActivity = (anchor: string): LessonActivityAnchorModel | null => {
    if (anchor == null) return null;
    return (
      state.lessonActivityAnchors.find((model) => String(model.anchor) === String(anchor)) || null
    );
  };

  const getAnchorPage = (anchor: string): LessonActivityComponentAnchorModel | null => {
    if (anchor == null) return null;
    return (
      state.lessonActivityComponentAnchors.find(
        (model) => String(model.anchor) === String(anchor),
      ) || null
    );
  };

  const getAnchorNotebookPage = (anchor) => {
    if (anchor == null) return null;
    return (
      state.notebookPageAnchors.find((model) => String(model.localId) === String(anchor)) || null
    );
  };

  const getActivityByAnchorPage = (anchor: string): LessonActivityAnchorModel | null => {
    if (anchor == null) return null;

    const page: LessonActivityComponentAnchorModel | null =
      state.lessonActivityComponentAnchors.find(
        (model) => String(model.anchor) === String(anchor),
      ) || null;
    if (isEmpty(page) || !page) return null;

    return state.lessonActivityAnchors.find((model) => model.id === page.pageId) ?? null;
  };

  const getSortedAnchorSections = () => {
    const sections = state.lessonActivityAnchors.filter((model) => model.parentId === null);
    return sections.sort(pageSortFunc);
  };

  const getSortedAnchorActivities = (sectionId) => {
    const activities = state.lessonActivityAnchors.filter((model) => model.parentId === sectionId);
    return activities.sort(pageSortFunc);
  };

  const getSortedAnchorPages = (anchor) => {
    const activity = state.lessonActivityAnchors.find((model) => model.anchor === anchor);
    if (isEmpty(activity)) return [];

    const pages = state.lessonActivityComponentAnchors.filter(
      (model) => model.pageId === activity.id,
    );
    return pages.sort(componentSortFunc);
  };
  const fetchLessonAnchor = (lessonUuid) => {
    const promise = LessonEditorApi.fetchLessonAnchors(lessonUuid);
    promise.then((response) => {
      if (response.code !== "ok") {
        throw response.errorMap;
      }
      state.lessonUuid = lessonUuid;
      const body = response.data;
      state.lessonActivityAnchors = body.lessonActivityAnchorModels ?? [];
      state.lessonActivityComponentAnchors = body.lessonActivityComponentAnchorModels ?? [];
      state.showSectionActivityNumber = body.showSectionActivityNumber;
      state.showCover = body.showCover;
      state.showSectionCover = body.showSectionCover;
    });
    return promise;
  };
  /**
   * refresh Lesson Anchor of the current lesson if exists
   */
  const refreshLessonAnchor = () => {
    if (state.lessonUuid) {
      fetchLessonAnchor(state.lessonUuid);
    }
  };

  const fetchNotebookAnchor = (notebookUuid) => {
    const promise = NotebookApi.fetchNotebookAnchor(notebookUuid);
    promise.then((response) => {
      if (response.code !== "ok") {
        throw response.errorMap;
      }
      state.notebookUuid = notebookUuid;
      const body = response.data.map((page, index) => {
        return { ...page, indexNo: index };
      });
      state.notebookPageAnchors = body ?? [];
    });
    return promise;
  };
  /**
   * refresh Notebook Anchor of the current notebook if exists
   */
  const refreshNotebookAnchor = () => {
    if (state.notebookUuid) {
      fetchNotebookAnchor(state.notebookUuid);
    }
  };
  /**
   * send a navigation event for watchers to react
   * @param type Link_TYPE
   * @param id
   */
  const setNavigationEvent = ({ type, id }) => {
    const navigation = { type, id };
    state.navigation = navigation;
  };
  // === Expose store ===
  return {
    state, // expose if needed
    getShowSectionActivityNumber,
    getShowCover,
    getShowSectionCover,
    getLessonUuid,
    getNotebookUuid,
    getNavigationEvent,
    getSortedAnchorNotebookPages,
    getAnchorActivity,
    getAnchorPage,
    getAnchorNotebookPage,
    getActivityByAnchorPage,
    getSortedAnchorSections,
    getSortedAnchorActivities,
    getSortedAnchorPages,
    fetchLessonAnchor,
    refreshLessonAnchor,
    fetchNotebookAnchor,
    refreshNotebookAnchor,
    setNavigationEvent,
  };
});
